import * as React from 'react';
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from 'convex/react';
import { FragmentsLogo } from '@fragments-sdk/ui';
import { api } from '@/convex';
import { useClerkUser } from '@/hooks/useClerkUser';
import { OrgProvider } from '@/hooks/useOrgContext';

export const Route = createFileRoute('/_authed')({
  component: AuthedLayout,
});

function AuthedLayout() {
  const { user, isLoaded: isUserLoaded } = useClerkUser();
  const navigate = useNavigate();
  const clerkUserId = user?.id;

  const onboarding = useQuery(
    api.onboarding.getByUser,
    clerkUserId ? { clerkUserId } : 'skip',
  );
  const memberships = useQuery(
    api.memberships.listByUser,
    clerkUserId ? { clerkUserId } : 'skip',
  );

  // Redirect to /sign-in if not authenticated
  React.useEffect(() => {
    if (!isUserLoaded) return;
    if (!user) {
      navigate({ to: '/sign-in', replace: true });
    }
  }, [isUserLoaded, user, navigate]);

  // Sync user profile for display name resolution
  const syncProfile = useMutation(api.users.syncProfile);
  const profileSyncedRef = React.useRef(false);
  React.useEffect(() => {
    if (!user || profileSyncedRef.current) return;
    if (user.id.startsWith('test_user_')) return;
    profileSyncedRef.current = true;
    syncProfile({
      clerkUserId: user.id,
      displayName:
        user.fullName ?? user.primaryEmailAddress?.emailAddress ?? user.id,
      email: user.primaryEmailAddress?.emailAddress ?? undefined,
      avatarUrl: user.imageUrl ?? undefined,
    }).catch(() => {
      // Profile sync is best-effort; don't block the UI
    });
  }, [user, syncProfile]);

  // Redirect to /onboarding if not completed
  React.useEffect(() => {
    if (
      !isUserLoaded ||
      !user ||
      onboarding === undefined ||
      memberships === undefined
    ) {
      return;
    }

    const hasMemberships = memberships.length > 0;
    if (onboarding && !onboarding.completedAt) {
      navigate({
        to: '/onboarding',
        search: { checkout: undefined },
        replace: true,
      });
      return;
    }

    if (!onboarding && !hasMemberships) {
      navigate({
        to: '/onboarding',
        search: { checkout: undefined },
        replace: true,
      });
    }
  }, [isUserLoaded, user, onboarding, memberships, navigate]);

  // Loading state while we check onboarding status
  if (
    !isUserLoaded ||
    !user ||
    onboarding === undefined ||
    memberships === undefined ||
    (onboarding !== null && !onboarding.completedAt) ||
    (!onboarding && memberships.length === 0)
  ) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100dvh',
          background: 'var(--fui-bg-primary)',
        }}
      >
        <FragmentsLogo size={48} className="fui-spin" />
        <style>{`
          .fui-spin {
            animation: fui-spin 1.2s ease-in-out infinite;
          }
          @keyframes fui-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <OrgProvider>
      <Outlet />
    </OrgProvider>
  );
}
